/**
 * Right-anchored slide-over that previews the active artifact.
 *
 * Replaces the older single-purpose ``SqlPreviewDrawer``. The header,
 * facts panel (output / status / dataset / anchors), knowledge citations,
 * and sharing/visibility control are unchanged from that earlier shell —
 * the body is now a tab switcher whose tab set depends on the artifact
 * kind (see {@link tabsForArtifact}):
 *
 *   - analytical variables (``av`` / ``av_denovo`` / ``composition``) →
 *       Description / SQL / Verification / Results
 *   - Concept groups (``concept_group``) →
 *       Description / [Definition if present] / Codes / Usage
 *
 * The active tab is controlled by the parent so the stream reducer can
 * snap the drawer to whichever surface a tool just produced (verify_analytical_variable →
 * Verification, etc.). Auto-opens when a new artifact arrives, and uses
 * ``variant="persistent"`` so it slides alongside the chat rather than
 * overlaying it — the epi can read the artifact and keep conversing.
 */

import CloseIcon from '@mui/icons-material/Close';
import { Box, Drawer, IconButton, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { useMemo } from 'react';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';

import { CodesTab } from './artifact-tabs/CodesTab';
import { ConceptsTab } from './artifact-tabs/ConceptsTab';
import { DecisionsTab } from './artifact-tabs/DecisionsTab';
import { DefinitionTab } from './artifact-tabs/DefinitionTab';
import { DescriptionTab } from './artifact-tabs/DescriptionTab';
import { RecallTab } from './artifact-tabs/RecallTab';
import { ResultsTab } from './artifact-tabs/ResultsTab';
import { SqlTab } from './artifact-tabs/SqlTab';
import {
  AV_CARD_RECALL_FLAG,
  resolveActiveTab,
  tabLabel,
  tabsForArtifact,
} from './artifact-tabs/tabsForArtifact';
import { UsageTab } from './artifact-tabs/UsageTab';
import { VerificationTab } from './artifact-tabs/VerificationTab';
import { ArtifactSharingControl } from './ArtifactSharingControl';
import { KnowledgeCitationsChip } from './KnowledgeCitationChip';
import { useKnowledgeBrowserStore } from '../state/knowledgeBrowserStore';
import type { EpiArtifact, EpiArtifactScope, EpiArtifactTab } from '../types/epi-chat-types';

interface ArtifactDrawerProps {
  artifact: EpiArtifact | null;
  open: boolean;
  onClose: () => void;
  /**
   * Tab the parent wants surfaced. ``null`` means "use the default for
   * this artifact's kind". When the parent's value isn't valid for the
   * current artifact (kind change while a tab was selected) the drawer
   * silently falls back to the default.
   */
  activeTab: EpiArtifactTab | null;
  onTabChange: (tab: EpiArtifactTab) => void;
  width?: number;
  /**
   * Optional optimistic-update hook for the sharing control. Forwarded to
   * ``ArtifactSharingControl`` so a successful promotion updates the
   * artifact's scope chip immediately, before the next session push.
   */
  onArtifactScopeChange?: (artifactId: string, scope: EpiArtifactScope) => void;
}

const renderTabBody = (
  artifact: EpiArtifact,
  tab: EpiArtifactTab,
  onJumpToSqlLine: (line: number) => void
) => {
  switch (tab) {
    case 'description':
      return <DescriptionTab artifact={artifact} />;
    case 'sql':
      return <SqlTab artifact={artifact} />;
    case 'verification':
      return <VerificationTab artifact={artifact} onJumpToSqlLine={onJumpToSqlLine} />;
    case 'results':
      return <ResultsTab artifact={artifact} />;
    case 'definition':
      return <DefinitionTab artifact={artifact} />;
    case 'codes':
      return <CodesTab artifact={artifact} />;
    case 'concepts':
      return <ConceptsTab artifact={artifact} />;
    case 'decisions':
      return <DecisionsTab artifact={artifact} />;
    case 'recall':
      return <RecallTab artifact={artifact} />;
    case 'usage':
      return <UsageTab artifact={artifact} />;
  }
};

export const ArtifactDrawer = ({
  artifact,
  open,
  onClose,
  activeTab,
  onTabChange,
  // Default width must match the grid column reserved for the drawer in
  // EpiChatPanelLayout. If they diverge, the drawer's fixed-position Paper
  // extends beyond the reserved column and overlays the chat content on
  // the right edge.
  width = 440,
  onArtifactScopeChange,
}: ArtifactDrawerProps) => {
  const openKnowledgeBrowser = useKnowledgeBrowserStore((s) => s.open);
  // The manifest-enriched tabs (Concepts / Decisions / Recall) are
  // rolled out behind ``av_card_recall`` in addition to the gen_3
  // gate the rest of the chat surface lives under, so the two can be
  // enabled / disabled independently while the citation + binding
  // pipelines mature. Renderer reads the flag; the helpers stay pure.
  // ``useFeatureFlagSplit`` returns ``string | boolean`` (Split's
  // ``treatment`` API can carry richer payloads); coerce to a plain
  // bool so the helper's ``avCardRecallEnabled?: boolean`` type stays
  // clean and any truthy treatment turns the tabs on.
  const avCardRecallEnabled = Boolean(useFeatureFlagSplit(AV_CARD_RECALL_FLAG));
  const tabOptions = useMemo(() => ({ avCardRecallEnabled }), [avCardRecallEnabled]);

  const tabs = useMemo(() => tabsForArtifact(artifact, tabOptions), [artifact, tabOptions]);
  const resolvedTab = useMemo(
    () => resolveActiveTab(artifact, activeTab, tabOptions),
    [artifact, activeTab, tabOptions]
  );

  const handleJumpToSqlLine = (_line: number) => {
    // Switching tabs is enough for now — the SyntaxHighlighter doesn't
    // expose per-line scroll. Hook is in place so a future PR can scroll
    // to the line number once we replace the renderer.
    onTabChange('sql');
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="persistent"
      PaperProps={{
        sx: {
          width,
          borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <Stack sx={{ height: '100%' }}>
        {/* Compressed header — single title row + one dense chip strip
            below it. Slug, coreLabel, and the multi-row facts panel from
            the previous layout are folded into the chip strip so the tab
            content surfaces ~70-90px earlier than before. The dataset
            chip is intentionally absent here: dataset is now an
            execution-time pin (Verification tab), not a property of the
            variable itself. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            paddingX: 1.5,
            paddingY: 0.75,
            gap: 1,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            backgroundColor: (theme) => theme.palette.action.hover,
          }}
        >
          <Stack sx={{ minWidth: 0, flex: 1, gap: 0.25 }}>
            <Tooltip
              title={artifact?.name ?? ''}
              placement="bottom-start"
              disableHoverListener={!artifact?.name}
            >
              <Typography variant="subtitle2" noWrap>
                {artifact?.dataElement ?? 'Preview'}
              </Typography>
            </Tooltip>
            {/* Single dense metadata line — kind, output type, core label,
                anchors. Plain typography for informational bits; only
                navigable items (anchor links, citations, sharing) get a
                heavier shape. Keeps the header to two lines max even with
                a long anchor slug. */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}
            >
              <Box component="span" sx={{ fontWeight: 600, letterSpacing: '0.04em' }}>
                {artifact?.kind ? artifact.kind.replace('_', ' ').toUpperCase() : 'ARTIFACT'}
              </Box>
              {artifact?.format && <Box component="span">· {artifact.format}</Box>}
              {artifact?.coreLabel && <Box component="span">· {artifact.coreLabel}</Box>}
              {artifact?.artifactStatus && artifact.artifactStatus !== 'drafting' && (
                <Box
                  component="span"
                  sx={{
                    color:
                      artifact.validationBlocking || artifact.artifactStatus === 'blocked'
                        ? 'error.main'
                        : 'success.main',
                    fontWeight: 600,
                  }}
                >
                  {/* Wire-format status is 'validated' | 'blocked' | 'drafting'.
                      User-facing copy uses "verified" — keeps chat / chip
                      vocabulary aligned with the verify_analytical_variable tool. */}
                  · {artifact.artifactStatus === 'validated' ? 'verified' : artifact.artifactStatus}
                </Box>
              )}
              {artifact?.status === 'streaming' && (
                <Box component="span" sx={{ fontStyle: 'italic' }}>
                  · streaming
                </Box>
              )}
              {(artifact?.sources?.length ?? 0) > 0 && (
                <>
                  <Box component="span" sx={{ fontWeight: 600, letterSpacing: '0.04em' }}>
                    · anchors:
                  </Box>
                  {artifact?.sources?.map((sourceSlug) => (
                    <Box
                      key={sourceSlug}
                      component="button"
                      onClick={() => openKnowledgeBrowser(`av.${sourceSlug}`)}
                      sx={{
                        border: 0,
                        padding: 0,
                        background: 'none',
                        cursor: 'pointer',
                        fontFamily: 'monospace',
                        fontSize: 'inherit',
                        color: (theme) => theme.palette.primary.main,
                        textDecoration: 'underline',
                        textUnderlineOffset: 2,
                        '&:hover': { color: (theme) => theme.palette.primary.dark },
                        '&:focus-visible': {
                          outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                          outlineOffset: 1,
                          borderRadius: 1,
                        },
                      }}
                      data-testid={`epi-artifact-drawer-anchor-${sourceSlug}`}
                      aria-label={`Preview anchor analytical variable ${sourceSlug}`}
                    >
                      {sourceSlug}
                    </Box>
                  ))}
                </>
              )}
            </Typography>
            {/* Navigable affordances row — citations open the knowledge
                browser; sharing control toggles scope. Both are
                interactive, so they keep their chip shape. Rendered in a
                separate row so they don't compete with the dense meta
                line above. */}
            {artifact &&
              ((artifact.knowledgeApplied?.length ?? 0) > 0 ||
                artifact.kind === 'av' ||
                artifact.kind === 'av_denovo') && (
                <Stack
                  direction="row"
                  alignItems="center"
                  flexWrap="wrap"
                  sx={{ gap: 0.5, marginTop: 0.25 }}
                >
                  {artifact.knowledgeApplied && artifact.knowledgeApplied.length > 0 && (
                    <KnowledgeCitationsChip entries={artifact.knowledgeApplied} />
                  )}
                  {(artifact.kind === 'av' || artifact.kind === 'av_denovo') && (
                    <ArtifactSharingControl
                      key={artifact.id}
                      slug={artifact.name}
                      scope={artifact.scope}
                      onScopeChange={
                        onArtifactScopeChange
                          ? (scope) => onArtifactScopeChange(artifact.id, scope)
                          : undefined
                      }
                    />
                  )}
                </Stack>
              )}
          </Stack>
          {/* Copy-to-clipboard moved into the SQL tab — putting it here was
              ambiguous (copy what?). Close stays as the only header action. */}
          <Tooltip title="Close preview">
            <IconButton size="small" onClick={onClose} aria-label="Close preview">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {artifact && tabs.length > 0 && (
          <Tabs
            value={resolvedTab}
            onChange={(_, value) => onTabChange(value as EpiArtifactTab)}
            variant="scrollable"
            scrollButtons={false}
            sx={{
              minHeight: 36,
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              '& .MuiTab-root': {
                minHeight: 36,
                paddingY: 0.5,
                textTransform: 'none',
                fontSize: (theme) => theme.typography.body2.fontSize,
              },
            }}
            data-testid="artifact-drawer-tabs"
          >
            {tabs.map((tab) => (
              <Tab
                key={tab}
                value={tab}
                label={tabLabel(tab)}
                data-testid={`artifact-drawer-tab-${tab}`}
              />
            ))}
          </Tabs>
        )}

        <Box sx={{ flex: 1, overflow: 'auto', padding: 0 }}>
          {artifact ? (
            renderTabBody(artifact, resolvedTab, handleJumpToSqlLine)
          ) : (
            <Box sx={{ padding: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No artifact selected yet. The first analytical variable or concept group the agent
                produces will open here.
              </Typography>
            </Box>
          )}
        </Box>
      </Stack>
    </Drawer>
  );
};
